// SB6-ANA-VIEW-05 — pointer-drag a floating element and keep it clamped
// inside its positioned ancestor (the Viewer cell). Used by `ViewerToolbar`.
//
//   * Drag is driven from a handle's `onPointerDown` (primary button only).
//   * While dragging, window `pointermove`/`pointerup` track the gesture so
//     the pointer can leave the handle without dropping the drag.
//   * Every proposed position is clamped so the element's rect stays fully
//     within the offsetParent's content box (Christian's "inside the viewer
//     widget" — Q4/AC4).
//   * The resting position is persisted to `localStorage` (Q5=b) so a reload
//     restores it; no contract / server round-trip.
//
// Keyboard repositioning (WCAG 2.5.7) is intentionally NOT wired here —
// Christian chose drag-only for now (Q7=b); the handle is still a focusable
// button with an accessible name so axe stays clean.
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export interface Point {
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

/** Keep the toolbar at least this far from every edge of the container. */
const EDGE_MARGIN_PX = 8;

/**
 * Pin `pos` so an element of `element` size stays fully inside a `container`
 * of the given size, never closer than `margin` to any edge. Pure — unit
 * tested. When the container is smaller than the element + margins, the
 * element pins to the top-left margin rather than going negative.
 */
export function clampPosition(
  pos: Point,
  element: Size,
  container: Size,
  margin = 0,
): Point {
  const maxX = Math.max(margin, container.width - element.width - margin);
  const maxY = Math.max(margin, container.height - element.height - margin);
  const clamp = (v: number, lo: number, hi: number): number =>
    v < lo ? lo : v > hi ? hi : v;
  return { x: clamp(pos.x, margin, maxX), y: clamp(pos.y, margin, maxY) };
}

function load(key: string | undefined, fallback: Point): Point {
  if (!key || typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<Point>;
    if (typeof parsed.x === "number" && typeof parsed.y === "number") {
      return { x: parsed.x, y: parsed.y };
    }
  } catch {
    /* malformed / storage disabled — fall through to default */
  }
  return fallback;
}

function save(key: string | undefined, pos: Point): void {
  if (!key || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(pos));
  } catch {
    /* quota or storage-disabled — best-effort */
  }
}

export interface UseDraggableWithinBoundsOptions {
  /** Starting position (offset from the container's top-left) before any drag. */
  defaultPosition: Point;
  /** `localStorage` key for persistence (Q5=b). Omit to keep it session-only. */
  storageKey?: string;
}

export interface UseDraggableWithinBoundsApi {
  /** Current `{ left, top }` offset to apply to the element. */
  position: Point;
  /** True between pointer-down on the handle and pointer-up. */
  isDragging: boolean;
  /** Attach to the draggable element — measured for clamping. */
  elementRef: React.RefObject<HTMLDivElement | null>;
  /** Attach to the drag handle's `onPointerDown`. */
  onHandlePointerDown: (event: React.PointerEvent) => void;
}

export function useDraggableWithinBounds({
  defaultPosition,
  storageKey,
}: UseDraggableWithinBoundsOptions): UseDraggableWithinBoundsApi {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<Point>(() =>
    load(storageKey, defaultPosition),
  );
  const [isDragging, setIsDragging] = useState(false);

  // Keep the latest position in a ref so the pointer-up handler can persist
  // it without re-subscribing the window listeners on every move.
  const positionRef = useRef(position);
  positionRef.current = position;
  const dragStart = useRef<{
    px: number;
    py: number;
    ox: number;
    oy: number;
  } | null>(null);

  /** Measure the element + its positioned ancestor's content box. */
  const measure = useCallback((): {
    element: Size;
    container: Size;
  } | null => {
    const el = elementRef.current;
    const parent = el?.offsetParent as HTMLElement | null;
    if (!el || !parent) return null;
    return {
      element: { width: el.offsetWidth, height: el.offsetHeight },
      container: { width: parent.clientWidth, height: parent.clientHeight },
    };
  }, []);

  const onHandlePointerDown = useCallback((event: React.PointerEvent): void => {
    if (event.button !== 0) return; // primary button only
    dragStart.current = {
      px: event.clientX,
      py: event.clientY,
      ox: positionRef.current.x,
      oy: positionRef.current.y,
    };
    setIsDragging(true);
  }, []);

  // Window-level tracking while a drag is active.
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (event: PointerEvent): void => {
      const start = dragStart.current;
      if (!start) return;
      const next: Point = {
        x: start.ox + (event.clientX - start.px),
        y: start.oy + (event.clientY - start.py),
      };
      const m = measure();
      setPosition(
        m ? clampPosition(next, m.element, m.container, EDGE_MARGIN_PX) : next,
      );
    };
    const onUp = (): void => {
      setIsDragging(false);
      dragStart.current = null;
      save(storageKey, positionRef.current);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [isDragging, measure, storageKey]);

  // Clamp back into view on mount and whenever the container resizes — a
  // persisted position from a larger window must not leave the toolbar
  // stranded off-screen.
  useLayoutEffect(() => {
    const el = elementRef.current;
    const parent = el?.offsetParent as HTMLElement | null;
    if (!el || !parent) return;
    const reclamp = (): void => {
      const m = measure();
      if (!m) return;
      setPosition((prev) =>
        clampPosition(prev, m.element, m.container, EDGE_MARGIN_PX),
      );
    };
    reclamp();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(reclamp);
    ro.observe(parent);
    return () => {
      ro.disconnect();
    };
  }, [measure]);

  return { position, isDragging, elementRef, onHandlePointerDown };
}
